import styled from "@emotion/styled"
import {
  AccordionDetails,
  AccordionSummary,
  Button,
  Typography,
} from "@mui/material"
import Image from "next/image"
import { Fragment, useState } from "react"
import { Desktop, useIsDesktop } from "ui"
import { sharedColors } from "ui/theme/colors"
import {
  applicationContactClick,
  getNonUmbracoUrl,
  Link,
  replaceRichTextLinks,
  type AccordionItem,
} from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import { StyledAccordion } from "../../Common/FaqItem"
import LocationCard from "./LocationCard"

const StyledAccordionSummary = styled(AccordionSummary)`
  &.MuiAccordionSummary-root {
    padding: 24px !important;
  }
`

const StyledAccordionDetails = styled(AccordionDetails)`
  &.MuiAccordionDetails-root {
    padding: 24px 16px !important;
    ${({ theme }) => theme.breakpoints.up("md")} {
      padding: 24px !important;
    }
  }
`

const ContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 40px;
  }
`

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const IconTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const FlexRow = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  justify-content: space-between;
  ${({ theme }) => theme.breakpoints.up("md")} {
    justify-content: unset;
  }
`

const HoursGrid = styled.div`
  display: grid;
  grid-template-columns: fit-content(55%) auto;
  column-gap: 32px;
  row-gap: 16px;
  align-items: start;
`

const ImportantBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 12px;
  background: rgba(27, 189, 119, 0.08);
  padding: 16px;
  ul {
    padding-inline-start: 24px;
  }
`

const DetailsFlex = styled.div<{ isSmall?: boolean }>`
  display: flex;

  flex-direction: column;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: ${({ isSmall }) => (isSmall ? "24px" : "48px")};
    align-items: center;
    flex-direction: row;
  }
`

const IconLink = styled(Link)`
  text-decoration: none;
`

const VerticalDivider = styled.div<{ isSmall?: boolean }>`
  display: none;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: block;
    width: 1px;
    height: ${({ isSmall }) => (isSmall ? "49px" : "60px")};
    background: ${sharedColors.borderGray};
  }
`

const IconText = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`
const IconBox = styled.div`
  padding: 8px;
  border-radius: 12px;
  border: 1px solid ${sharedColors.brandDark};
  display: flex;
  align-items: center;
  justify-content: center;
`

const IconLinkBig = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  text-decoration: none;
  p {
    width: max-content;
    max-width: 100%;
  }
`

const BottomContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
    justify-content: space-between;
  }
`

const ContactDetailsGrid = styled("div", {
  shouldForwardProp: (prop) => prop !== "columns",
})<{ columns: number }>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: grid;
    grid-template-columns: ${({ columns }) =>
      columns <= 1 ? "auto" : `auto repeat(${columns - 1}, minmax(0, 1fr))`};
    justify-content: start;
    align-items: stretch;
    gap: 24px;
  }
`

const ContactDetailSection = styled("div", {
  shouldForwardProp: (prop) => prop !== "hasBorder" && prop !== "empty",
})<{ hasBorder?: boolean; empty?: boolean }>`
  display: ${({ empty }) => (empty ? "none" : "flex")};
  flex-direction: column;
  gap: 16px;
  min-width: 0;

  ${({ theme }) => theme.breakpoints.up("md")} {
    display: ${({ empty }) => (empty ? "none" : "flex")};
    border-block-end: none;
    padding-block-end: 0;
    border-inline-start: ${({ hasBorder }) =>
      hasBorder ? `1px solid ${sharedColors.borderGray}` : "none"};
    padding-inline-start: ${({ hasBorder }) => (hasBorder ? "24px" : "0")};
  }
`

const ContactDetailHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const CappedTypography = styled(Typography)`
  /* text-box-trim: trim-both;
  text-box-edge: cap alphabetic; */
`

const DayColumn = styled(CappedTypography)`
  min-width: 110px;
  flex-shrink: 0;
`

const LocationCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`

const StyledButton = styled(Button)`
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 300px;
  }
`
export default function AccordionItemComp({
  item,
  isExpanded,
  onToggle,
  hideButton = false,
}: {
  item: AccordionItem
  index: number
  isExpanded: boolean
  onToggle: () => void
  hideButton?: boolean
}) {
  const isDesktop = useIsDesktop()
  const [expandedCard, setExpandedCard] = useState<number | null>(null)

  const handleCardToggle = (cardIndex: number) => {
    setExpandedCard(expandedCard === cardIndex ? null : cardIndex)
  }

  const hasParentOpeningHours = Boolean(
    item.openingHoursTitle || item.openingHoursContent.length > 0,
  )
  const hasParentBreakHours = Boolean(
    item.breakHoursTitle || item.breakHoursContent.length > 0,
  )
  const hasParentAddress = Boolean(
    item.addressTitle ||
      item.address ||
      (item.addressLink && item.addressLink.length > 0),
  )
  const hasParentFax = Boolean(item.faxTitle || item.faxNumber)
  const hasParentContactDetails =
    hasParentOpeningHours ||
    hasParentBreakHours ||
    hasParentAddress ||
    hasParentFax
  const visibleSectionCount =
    [
      hasParentOpeningHours,
      hasParentBreakHours,
      hasParentAddress,
      hasParentFax,
    ].filter(Boolean).length || 1

  return (
    <StyledAccordion onChange={onToggle} expanded={isExpanded}>
      <StyledAccordionSummary
        expandIcon={
          isExpanded ? (
            <Image
              alt=""
              width={24}
              height={24}
              src={"/icons/accordionClose.svg"}
            />
          ) : (
            <Image
              alt=""
              width={24}
              height={24}
              src={"/icons/accordionOpen.svg"}
            />
          )
        }
      >
        <Typography fontWeight={700} lineHeight={"140%"}>
          {item.itemTitle}
        </Typography>
      </StyledAccordionSummary>
      <StyledAccordionDetails>
        <ContentLayout>
          {item.importantToKnowTitle && (
            <ImportantBox>
              <Typography variant="body1" component={"h4"} fontWeight={700}>
                {item.importantToKnowTitle}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  "& p": {
                    margin: 0,
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: replaceRichTextLinks(
                    item.importantToKnowContent || "",
                  ),
                }}
              />
            </ImportantBox>
          )}

          {hasParentContactDetails && (
            <ContactDetailsGrid columns={visibleSectionCount}>
              <ContactDetailSection
                hasBorder={false}
                empty={!hasParentOpeningHours}
              >
                {hasParentOpeningHours && (
                  <>
                    <ContactDetailHeader>
                      {item.openingHoursIcon && (
                        <Image
                          alt=""
                          width={20}
                          height={20}
                          src={getUmbracoMediaUrl(item.openingHoursIcon) || ""}
                        />
                      )}
                      <CappedTypography variant="body2" fontWeight={700}>
                        {item.openingHoursTitle}
                      </CappedTypography>
                    </ContactDetailHeader>
                    <HoursGrid>
                      {item.openingHoursContent.map((el, i) => (
                        <Fragment key={i}>
                          <DayColumn variant="body2">{el.day}</DayColumn>
                          <CappedTypography variant="body2">
                            {el.hour}
                          </CappedTypography>
                        </Fragment>
                      ))}
                    </HoursGrid>
                  </>
                )}
              </ContactDetailSection>

              <ContactDetailSection hasBorder empty={!hasParentBreakHours}>
                {hasParentBreakHours && (
                  <>
                    <ContactDetailHeader>
                      {item.breakHoursIcon && (
                        <Image
                          alt=""
                          width={20}
                          height={20}
                          src={getUmbracoMediaUrl(item.breakHoursIcon) || ""}
                        />
                      )}
                      <CappedTypography variant="body2" fontWeight={700}>
                        {item.breakHoursTitle}
                      </CappedTypography>
                    </ContactDetailHeader>
                    {item.breakHoursContent.map((el, i) => (
                      <CappedTypography key={i} variant="body2">
                        {el.hour}
                      </CappedTypography>
                    ))}
                  </>
                )}
              </ContactDetailSection>

              <ContactDetailSection hasBorder empty={!hasParentAddress}>
                {hasParentAddress && (
                  <>
                    <ContactDetailHeader>
                      {item.addressIcon && (
                        <Image
                          alt=""
                          width={20}
                          height={20}
                          src={getUmbracoMediaUrl(item.addressIcon) || ""}
                        />
                      )}
                      {item.addressTitle && (
                        <CappedTypography variant="body2" fontWeight={700}>
                          {item.addressTitle}
                        </CappedTypography>
                      )}
                    </ContactDetailHeader>
                    {item.address && (
                      <CappedTypography
                        variant="body2"
                        sx={{
                          textDecoration: "underline",
                          whiteSpace: "pre-wrap",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: replaceRichTextLinks(item.address),
                        }}
                      />
                    )}
                    {item.addressLink && item.addressLink.length > 0 && (
                      <Link
                        href={item.addressLink[0].url || "#"}
                        target={item.addressLink[0].target || undefined}
                      >
                        <CappedTypography
                          variant="body2"
                          sx={{
                            textDecoration: "underline",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {item.addressLink[0].name}
                        </CappedTypography>
                      </Link>
                    )}
                  </>
                )}
              </ContactDetailSection>

              <ContactDetailSection hasBorder empty={!hasParentFax}>
                {hasParentFax && (
                  <>
                    <ContactDetailHeader>
                      {item.faxIcon && (
                        <Image
                          alt=""
                          width={20}
                          height={20}
                          src={getUmbracoMediaUrl(item.faxIcon) || ""}
                        />
                      )}
                      <CappedTypography variant="body2" fontWeight={700}>
                        {item.faxTitle}
                      </CappedTypography>
                    </ContactDetailHeader>
                    {item.faxNumber && (
                      <CappedTypography variant="body2">
                        {item.faxNumber}
                      </CappedTypography>
                    )}
                  </>
                )}
              </ContactDetailSection>
            </ContactDetailsGrid>
          )}

          <LocationCardsContainer>
            {item.itemsList.map((innerItem, idx) => (
              <LocationCard
                key={idx}
                item={innerItem}
                isExpanded={expandedCard === idx}
                onToggle={() => handleCardToggle(idx)}
              />
            ))}
          </LocationCardsContainer>

          <FlexColumn>
            {item.documentsIcon && item.documentsTitle && (
              <IconTitle>
                {item.documentsIcon && (
                  <Image
                    alt=""
                    width={20}
                    height={20}
                    src={getUmbracoMediaUrl(item.documentsIcon) || ""}
                  />
                )}
                <Typography variant="body1" fontWeight={500} component={"h4"}>
                  {item.documentsTitle}
                </Typography>
              </IconTitle>
            )}
            <FlexColumn>
              {item.documents.map((el, i) => (
                <FlexRow key={i}>
                  <Typography variant="body2" minWidth={150}>
                    {el.name}
                  </Typography>
                  <IconLink href={getUmbracoMediaUrl(el?.url)} target="_blank">
                    <Image
                      alt=""
                      width={24}
                      height={24}
                      src={"/icons/download.svg"}
                    />
                  </IconLink>
                </FlexRow>
              ))}
            </FlexColumn>
          </FlexColumn>
          <BottomContainer>
            <DetailsFlex isSmall>
              {item.phoneNumber[0]?.url && (
                <IconText>
                  <IconLinkBig href={item.phoneNumber[0]?.url || ""}>
                    <IconBox>
                      <Image
                        alt=""
                        width={24}
                        height={24}
                        src={"/icons/phone.svg"}
                      />
                    </IconBox>
                    <Typography variant="body2">
                      {item.phoneNumber[0]?.name}
                    </Typography>
                  </IconLinkBig>
                </IconText>
              )}
              {item.emailAddress[0]?.url && (
                <Desktop>
                  <VerticalDivider isSmall />
                </Desktop>
              )}
              {item.emailAddress[0]?.url && (
                <IconText>
                  <IconLinkBig href={item.emailAddress[0]?.url || ""}>
                    <IconBox>
                      <Image
                        alt=""
                        width={24}
                        height={24}
                        src={"/icons/email.svg"}
                      />
                    </IconBox>
                    <Typography variant="body2">
                      {item.emailAddress[0]?.name}
                    </Typography>
                  </IconLinkBig>
                </IconText>
              )}
            </DetailsFlex>
            {!hideButton &&
            item?.button?.url &&
            item?.button?.url?.length > 0 ? (
              <StyledButton
                variant="secondary"
                LinkComponent={Link}
                href={getNonUmbracoUrl(item.button?.url)}
                onClick={() => {
                  applicationContactClick(
                    item.button.name || "",
                    item.button.url || "",
                  )
                }}
              >
                {item.button.name}
              </StyledButton>
            ) : null}
          </BottomContainer>
        </ContentLayout>
      </StyledAccordionDetails>
    </StyledAccordion>
  )
}
